/**
 * Diagnostic fields + copy-to-clipboard button. The header line is
 * optional so the same body can sit inside a popover (with its own header
 * line) or a dialog (where the dialog title fills that role).
 */

import React, { useCallback, useMemo, useState } from "react";
import {
  collectGpuDiagnostic,
  formatDiagnosticForCopy,
  formatMB,
  type GpuDiagnostic,
} from "../../utils/gpuDiagnostic";
import type { BlockerCode } from "../../utils/reconstructionBlocker";
import type { GpuVerdict } from "../../utils/gpuVerdict";
import { logger } from "../../utils/logger";

interface DiagnosticBodyProps {
  verdict: GpuVerdict;
  blockerCode: BlockerCode | null;
  /** Optional header line. The badge popover uses "Viewport mode: 2D (Stack)";
   *  the global dialog passes its own title via the dialog header instead. */
  header?: string;
  /** User-facing reason for stack mode. Callers compute via
   *  `decideViewport(meta, verdict).reason` (badge falls back to
   *  "Non-volumetric" since it knows routing fired; dialog passes the
   *  helper output unchanged). Omit to skip the Reason row. */
  reasonLine?: string;
}

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="text-fg-muted shrink-0 w-28">{label}</span>
    <span className="text-fg break-all">{value}</span>
  </div>
);

export const DiagnosticBody: React.FC<DiagnosticBodyProps> = ({
  verdict,
  blockerCode,
  header,
  reasonLine,
}) => {
  const diag: GpuDiagnostic = useMemo(
    () => collectGpuDiagnostic({ verdict, blockerCode }),
    [verdict, blockerCode],
  );

  const [copyState, setCopyState] = useState<"idle" | "ok" | "err">("idle");

  const handleCopy = useCallback(() => {
    const text = formatDiagnosticForCopy(diag, reasonLine);
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopyState("ok");
        window.setTimeout(() => setCopyState("idle"), 1500);
      })
      .catch((e) => {
        logger.warn("[DiagnosticBody] clipboard write failed:", e);
        setCopyState("err");
        window.setTimeout(() => setCopyState("idle"), 1500);
      });
  }, [diag, reasonLine]);

  return (
    <div className="space-y-2 text-xs">
      {header && <div className="font-medium text-fg">{header}</div>}
      <div className="space-y-1">
        {reasonLine && <Field label="Reason" value={reasonLine} />}
        <Field label="GPU verdict" value={diag.verdict} />
        <Field label="Renderer" value={diag.renderer ?? "unavailable"} />
        <Field
          label="Image cache"
          value={`${formatMB(diag.cacheBytesUsed)} / ${formatMB(diag.cacheBytesMax)}`}
        />
      </div>
      <button
        type="button"
        onClick={handleCopy}
        className="w-full bg-surface-2 hover:bg-surface text-fg rounded px-2 py-1 border border-border-strong transition-colors"
        data-testid="diagnostic-copy"
      >
        {copyState === "ok" ? "Copied" : copyState === "err" ? "Copy failed" : "Copy diagnostic"}
      </button>
    </div>
  );
};
